<script setup>
import { computed } from 'vue';
import SummaryList from './SummaryList.vue';
import CollapsibleSection from './CollapsibleSection.vue';
import { formatLabel, valueToText, valueToDisplayEntries, orderEntriesByPreferredKeys } from '../utils/format.js';
import { isScoreField } from '../utils/scoring.js';

const props = defineProps({
	reportMeta: { type: Object, required: true },
	validation: { type: Object, required: true },
	collapsed: { type: Boolean, required: true }
});

defineEmits(['update:collapsed']);

const REPORT_META_PREFERRED_KEYS = [
	'lab_name',
	'report_id',
	'report_number',
	'report_date',
	'customer',
	'farm',
	'field',
	'crop',
	'variety',
	'sample_count'
];

const VALIDATION_TOOLTIPS = {
	plausibility: 'Checks each reading against normal agronomic ranges for its nutrient — e.g. pH between 3.5–10, no negative ppm. Catches OCR misreads and unit errors.'
};

const reportMetaRows = computed(() => {
	const entries = Object.entries(props.reportMeta || {}).filter(([key]) => key !== 'ingestion');
	return orderEntriesByPreferredKeys(Object.fromEntries(entries), REPORT_META_PREFERRED_KEYS).map(([key, value]) => {
		const isScore = isScoreField(key);
		const isObject = value != null && typeof value === 'object';
		return {
			key,
			label: formatLabel(key),
			rawValue: value,
			isScore,
			text: valueToText(value),
			displayEntries: isObject ? valueToDisplayEntries(value) : null
		};
	});
});

const ingestionRows = computed(() => {
	const ingestion = props.reportMeta && props.reportMeta.ingestion;
	if (ingestion == null) return [];

	if (typeof ingestion !== 'object' || Array.isArray(ingestion)) {
		return [{
			key: 'ingestion',
			label: formatLabel('ingestion'),
			rawValue: ingestion,
			isScore: false,
			text: valueToText(ingestion),
			displayEntries: null
		}];
	}

	return Object.entries(ingestion).map(([key, value]) => {
		const isObject = value != null && typeof value === 'object';
		return {
			key,
			label: formatLabel(key),
			rawValue: value,
			isScore: isScoreField(key),
			text: valueToText(value),
			displayEntries: isObject ? valueToDisplayEntries(value) : null
		};
	});
});

const validationRows = computed(() => {
	const rows = Object.entries(props.validation).map(([key, value]) => {
		const isBoolean = typeof value === 'boolean';
		const isScore = isScoreField(key);
		const isObject = value != null && typeof value === 'object';
		let rowClass = '';
		if (key === 'warnings') rowClass = 'tnt-row-warnings';
		if (key === 'errors') rowClass = 'tnt-row-errors';

		return {
			key,
			label: formatLabel(key),
			rawValue: value,
			isBoolean,
			boolValue: value,
			isScore,
			rowClass,
			tooltip: VALIDATION_TOOLTIPS[key],
			text: valueToText(value),
			displayEntries: isObject ? valueToDisplayEntries(value) : null
		};
	});

	rows.push(...ingestionRows.value);
	return rows;
});

const hasReportMeta = computed(() => Object.keys(props.reportMeta || {}).length > 0);
const hasValidation = computed(() => Object.keys(props.validation || {}).length > 0);
const hasData = computed(() => hasReportMeta.value || hasValidation.value);
</script>

<template>
	<CollapsibleSection
		title="Report Meta &amp; Validation"
		:visible="hasData"
		:model-value="collapsed"
		@update:model-value="$emit('update:collapsed', $event)"
	>
		<div class="tnt-summary-grid">
			<SummaryList v-if="hasReportMeta" :rows="reportMetaRows" />
			<SummaryList v-if="hasValidation" :rows="validationRows" />
		</div>
	</CollapsibleSection>
</template>
